"use client"

import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Copy, ArrowRight } from "lucide-react"
import { toast } from "sonner"

type CaseType =
  | "lowercase"
  | "uppercase"
  | "titlecase"
  | "sentencecase"
  | "camelcase"
  | "pascalcase"
  | "snakecase"
  | "kebabcase"
  | "constantcase"
  | "dotcase"

interface CaseOption {
  id: CaseType
  name: string
  example: string
}

const CASE_OPTIONS: CaseOption[] = [
  { id: "lowercase", name: "lowercase", example: "hello world" },
  { id: "uppercase", name: "UPPERCASE", example: "HELLO WORLD" },
  { id: "titlecase", name: "Title Case", example: "Hello World" },
  { id: "sentencecase", name: "Sentence case", example: "Hello world" },
  { id: "camelcase", name: "camelCase", example: "helloWorld" },
  { id: "pascalcase", name: "PascalCase", example: "HelloWorld" },
  { id: "snakecase", name: "snake_case", example: "hello_world" },
  { id: "kebabcase", name: "kebab-case", example: "hello-world" },
  { id: "constantcase", name: "CONSTANT_CASE", example: "HELLO_WORLD" },
  { id: "dotcase", name: "dot.case", example: "hello.world" },
]

export function CaseConverter() {
  const [input, setInput] = useState("")
  const [output, setOutput] = useState("")
  const [selectedCase, setSelectedCase] = useState<CaseType>("lowercase")

  const toWords = (str: string): string[] => {
    return str
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_\-./]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
  }

  const convertCase = useCallback((text: string, caseType: CaseType): string => {
    const words = toWords(text)

    switch (caseType) {
      case "lowercase":
        return text.toLowerCase()
      case "uppercase":
        return text.toUpperCase()
      case "titlecase":
        return words
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ")
      case "sentencecase":
        return words
          .map((w, i) =>
            i === 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()
          )
          .join(" ")
      case "camelcase":
        return words
          .map((w, i) =>
            i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
          )
          .join("")
      case "pascalcase":
        return words
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join("")
      case "snakecase":
        return words.map((w) => w.toLowerCase()).join("_")
      case "kebabcase":
        return words.map((w) => w.toLowerCase()).join("-")
      case "constantcase":
        return words.map((w) => w.toUpperCase()).join("_")
      case "dotcase":
        return words.map((w) => w.toLowerCase()).join(".")
      default:
        return text
    }
  }, [])

  const handleConvert = useCallback(
    (caseType: CaseType) => {
      setSelectedCase(caseType)
      setOutput(convertCase(input, caseType))
    },
    [input, convertCase]
  )

  const copyToClipboard = useCallback(() => {
    if (!output) return
    navigator.clipboard.writeText(output)
    toast.success("Copied to clipboard!")
  }, [output])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Input Text</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Enter text to convert..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="min-h-[120px] font-mono"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Convert To</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {CASE_OPTIONS.map((option) => (
              <Button
                key={option.id}
                variant={selectedCase === option.id ? "default" : "outline"}
                onClick={() => handleConvert(option.id)}
                className="flex flex-col h-auto py-3"
              >
                <span className="font-medium">{option.name}</span>
                <span className="text-xs opacity-70">{option.example}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {output && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Result</CardTitle>
              <Button variant="outline" size="sm" onClick={copyToClipboard}>
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              className="min-h-[120px] font-mono"
            />
          </CardContent>
        </Card>
      )}

      {input && (
        <Card>
          <CardHeader>
            <CardTitle>All Conversions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {CASE_OPTIONS.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center justify-between p-2 bg-muted rounded-md"
                >
                  <div className="flex items-center gap-2 min-w-[120px]">
                    <span className="text-sm font-medium">{option.name}</span>
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <span className="font-mono text-sm flex-1 truncate px-2">
                    {convertCase(input, option.id)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(convertCase(input, option.id))
                      toast.success("Copied!")
                    }}
                    className="flex-shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
